import { useState } from "react";
import MainLayout from "@/components/feature/MainLayout";
import AlarmStats from "./components/AlarmStats";
import AlarmList from "./components/AlarmList";
import PushSettings from "./components/PushSettings";
import AlarmAIAnalysis from "./components/AlarmAIAnalysis";
import { AlarmLevel, AlarmType } from "@/mocks/alarm";

type TabKey = "list" | "ai" | "push";

const levelFilters: (AlarmLevel | "全部")[] = ["全部", "紧急", "严重", "警告", "提示"];
const statusFilters = ["全部", "未处理", "处理中", "已处理", "已忽略"];
const typeFilters: (AlarmType | "全部")[] = ["全部", "泄漏报警", "欠费预警", "设备异常", "压力异常", "流量异常", "安检逾期", "保险到期", "通信中断"];

export default function AlarmPage() {
  const [activeTab, setActiveTab] = useState<TabKey>("list");
  const [filterLevel, setFilterLevel] = useState<string>("全部");
  const [filterStatus, setFilterStatus] = useState<string>("全部");
  const [filterType, setFilterType] = useState<string>("全部");
  const [searchText, setSearchText] = useState("");

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">实时告警中心</h2>
            <p className="text-xs text-gray-400 mt-0.5">汇总所有用户泄漏报警、欠费预警、设备异常等告警信息，AI智能识别与分析</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
              <span className="text-xs text-red-600 font-medium">实时监控中</span>
            </div>
            <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <i className="ri-download-line"></i>导出记录
            </button>
          </div>
        </div>

        {/* Stats */}
        <AlarmStats />

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {[
            { key: "list", label: "告警列表", icon: "ri-alarm-warning-line" },
            { key: "ai", label: "AI智能分析", icon: "ri-robot-2-line" },
            { key: "push", label: "推送设置", icon: "ri-notification-3-line" },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as TabKey)}
              className={`flex items-center gap-1.5 px-5 py-2 text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab.key ? "bg-white text-gray-900 font-semibold" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "list" && (
          <>
            {/* Filters */}
            <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-48">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    placeholder="搜索用户名、告警内容、编号..."
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-400 whitespace-nowrap">告警级别：</span>
                {levelFilters.map(l => (
                  <button
                    key={l}
                    onClick={() => setFilterLevel(l)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                      filterLevel === l
                        ? l === "紧急" ? "bg-red-500 text-white border-red-500"
                          : l === "严重" ? "bg-orange-500 text-white border-orange-500"
                          : l === "警告" ? "bg-amber-500 text-white border-amber-500"
                          : l === "提示" ? "bg-teal-500 text-white border-teal-500"
                          : "bg-gray-700 text-white border-gray-700"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-400 whitespace-nowrap">处理状态：</span>
                {statusFilters.map(s => (
                  <button
                    key={s}
                    onClick={() => setFilterStatus(s)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                      filterStatus === s ? "bg-[#E8531A] text-white border-[#E8531A]" : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-400 whitespace-nowrap">告警类型：</span>
                {typeFilters.map(t => (
                  <button
                    key={t}
                    onClick={() => setFilterType(t)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                      filterType === t ? "bg-[#E8531A] text-white border-[#E8531A]" : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <AlarmList
              filterLevel={filterLevel}
              filterStatus={filterStatus}
              filterType={filterType}
              searchText={searchText}
            />
          </>
        )}

        {activeTab === "ai" && <AlarmAIAnalysis />}
        {activeTab === "push" && <PushSettings />}
      </div>
    </MainLayout>
  );
}
